/**
 * Living Legend (docs/design/tide.md): the key is part of the analysis, not
 * small print under it. Each item shows a swatch that matches the marks, a
 * stable label, a non-colour marker, a visible plain-language definition, and
 * the current value with its unit (and a share only when there is a real whole).
 *
 * Modes:
 *   static   a key, no controls
 *   toggle   each item shows / hides its series (aria-pressed = shown)
 *   isolate  each item focuses its series; pressing the focused one restores all
 * Every interactive mode has a "Show all" reset and an explicit
 * "No categories selected" state that is never read as "no data".
 *
 * Native buttons only: Enter and Space work, focus is visible, targets are at
 * least 40px tall. The legend never fetches, writes or filters records by
 * itself; `scope` says in words what a press changes.
 */
import React, { useId, useState } from 'react';
import { Marker } from './Marker';
import type { MarkerShape } from './tokens';

export interface LegendItem {
  key: string;                 // stable id, never an index
  label: string;               // stable visible label
  color: string;               // CSS color/var used by the marks
  marker: MarkerShape;         // non-color identifier
  glyph?: string;              // the code drawn when marker is 'glyph' (status words)
  description?: string;        // plain-language definition, visible (not hover-only)
  value?: number | null;       // current value; null = not recorded (rendered as "not recorded", never 0)
  unit?: string;               // e.g. "open records"
  share?: { of: number; label: string } | null; // only for real wholes; denominator label shown
}

export interface LivingLegendProps {
  title: string;
  items: LegendItem[];
  mode?: 'static' | 'toggle' | 'isolate';
  selected?: string[];
  onChange?(selected: string[]): void;
  scope?: string;
  /** Lay items in one column (for a legend beside a chart). */
  stacked?: boolean;
  /**
   * comfortable (default): each item is a small card.
   * compact: no cards; hairline-separated rows, marker / label / value / share
   * on one 40px line and the state + definition on a smaller second line. One
   * column. Use it for legends beside or under a chart.
   */
  density?: 'comfortable' | 'compact';
  className?: string;
}

export const NOT_RECORDED_TEXT = 'not recorded';

const fmt = (n: number) => n.toLocaleString('en-US');

export function legendValueText(item: Pick<LegendItem, 'value' | 'unit'>): string | null {
  if (item.value === undefined) return null;
  if (item.value === null) return NOT_RECORDED_TEXT;
  return item.unit ? `${fmt(item.value)} ${item.unit}` : fmt(item.value);
}

export function legendShareText(item: Pick<LegendItem, 'value' | 'share'>): string | null {
  if (!item.share || typeof item.value !== 'number' || !(item.share.of > 0)) return null;
  const pct = Math.round((item.value / item.share.of) * 100);
  return `${pct}% of ${item.share.label}`;
}

/** The share alone ("65%"), for compact rows whose denominator is stated once for the legend. */
export function legendSharePct(item: Pick<LegendItem, 'value' | 'share'>): string | null {
  if (!item.share || typeof item.value !== 'number' || !(item.share.of > 0)) return null;
  return `${Math.round((item.value / item.share.of) * 100)}%`;
}

type ItemState = 'static' | 'shown' | 'hidden' | 'focused' | 'dimmed' | 'all';

export default function LivingLegend({
  title, items, mode = 'static', selected, onChange, scope, stacked = false, density = 'comfortable', className = '',
}: LivingLegendProps) {
  const compact = density === 'compact';
  // Compact rows show "65%"; the denominator is then stated once, when every share has the same one.
  const shareLabels = Array.from(new Set(items.filter((i) => legendShareText(i) !== null).map((i) => (i.share as { label: string }).label)));
  const commonShare = compact && shareLabels.length === 1 ? shareLabels[0] : null;
  const allKeys = items.map((i) => i.key);
  const [inner, setInner] = useState<string[]>(allKeys);
  const current = selected ?? inner;
  const currentSet = new Set(current);
  const shownCount = items.filter((i) => currentSet.has(i.key)).length;
  const allShown = shownCount === items.length;
  const noneShown = items.length > 0 && shownCount === 0;
  const uid = useId();
  const titleId = `${uid}-title`;
  const interactive = mode !== 'static';

  const commit = (next: string[]) => {
    const keep = new Set(next);
    const ordered = allKeys.filter((k) => keep.has(k));
    if (selected === undefined) setInner(ordered);
    onChange?.(ordered);
  };

  const press = (key: string) => {
    if (mode === 'toggle') {
      commit(currentSet.has(key) ? current.filter((k) => k !== key) : [...current, key]);
    } else if (mode === 'isolate') {
      const isolatedHere = shownCount === 1 && currentSet.has(key) && items.length > 1;
      commit(isolatedHere ? allKeys : [key]);
    }
  };

  const stateOf = (key: string): ItemState => {
    if (!interactive) return 'static';
    if (mode === 'toggle') return currentSet.has(key) ? 'shown' : 'hidden';
    if (allShown) return 'all';
    return currentSet.has(key) ? 'focused' : 'dimmed';
  };

  const STATE_WORD: Record<ItemState, string> = {
    static: '', shown: 'Shown', hidden: 'Hidden', focused: 'Focused', dimmed: 'Dimmed', all: '',
  };

  const summary = mode === 'toggle'
    ? `Showing ${shownCount} of ${items.length}`
    : (allShown ? `All ${items.length} shown equally` : `Focused on ${items.filter((i) => currentSet.has(i.key)).map((i) => i.label).join(', ')}`);

  return (
    <div
      role="group"
      aria-labelledby={titleId}
      className={`tide-legend ${stacked ? 'tide-legend--stack' : ''} ${compact ? 'tide-legend--compact' : ''} ${className}`.replace(/\s+/g, ' ').trim()}
      data-mode={mode}
      data-density={density}
    >
      <div className="tide-legend__head">
        <p id={titleId} className="tide-legend__title">{title}</p>
      </div>
      {scope && <p className="tide-legend__scope">{scope}</p>}
      {commonShare && <p className="tide-legend__scope" data-testid="legend-share-basis">Shares are % of {commonShare}.</p>}
      <ul className="tide-legend__list">
        {items.map((item) => {
          const state = stateOf(item.key);
          const value = legendValueText(item);
          const share = commonShare ? legendSharePct(item) : legendShareText(item);
          const descId = `${uid}-${item.key}-desc`;
          const stateWord = STATE_WORD[state];
          if (compact) {
            const line2 = stateWord || item.description;
            const row = (
              <>
                {mode === 'toggle' && (
                  <span className="tide-legend__check" aria-hidden="true">
                    {state === 'shown' && (
                      <svg width="10" height="10" viewBox="0 0 10 10" focusable="false"><path d="M1.5 5.2 4 7.6 8.5 2.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    )}
                  </span>
                )}
                <Marker shape={item.marker} color={item.color} size={14} glyph={item.glyph} />
                <span className="tide-legend__label">{item.label}</span>
                <span className="tide-legend__value">
                  {value !== null && <span className={item.value === null ? 'tide-legend__value-na' : undefined}>{value}</span>}
                </span>
                <span className="tide-legend__pct">
                  {share}
                  {/* The denominator is shown once for the legend; each row still says it to assistive technology. */}
                  {share && commonShare && <span className="tide-sr"> of {commonShare}</span>}
                </span>
              </>
            );
            return (
              <li key={item.key} className="tide-legend__item" data-state={state} data-key={item.key}>
                {interactive ? (
                  <button
                    type="button"
                    className="tide-legend__row"
                    aria-pressed={mode === 'toggle' ? state === 'shown' : state === 'focused'}
                    aria-describedby={line2 ? descId : undefined}
                    onClick={() => press(item.key)}
                  >
                    {row}
                  </button>
                ) : (
                  <div className="tide-legend__row">{row}</div>
                )}
                {line2 && (
                  <p id={descId} className="tide-legend__desc">
                    {stateWord && <span className="tide-legend__state">{stateWord}</span>}
                    {stateWord && item.description ? ' · ' : ''}
                    {item.description && <span>{item.description}</span>}
                  </p>
                )}
              </li>
            );
          }
          const content = (
            <>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {mode === 'toggle' && (
                  <span className="tide-legend__check" aria-hidden="true">
                    {state === 'shown' && (
                      <svg width="10" height="10" viewBox="0 0 10 10" focusable="false"><path d="M1.5 5.2 4 7.6 8.5 2.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    )}
                  </span>
                )}
                <Marker shape={item.marker} color={item.color} size={14} glyph={item.glyph} />
              </span>
              <span style={{ minWidth: 0 }}>
                <span className="tide-legend__label">{item.label}</span>
                {STATE_WORD[state] && <span className="tide-legend__state">{STATE_WORD[state]}</span>}
              </span>
              {(value !== null || share !== null) ? (
                <span className="tide-legend__value">
                  {value !== null && (
                    <span className={item.value === null ? 'tide-legend__value-na' : undefined}>{value}</span>
                  )}
                  {share !== null && <span className="tide-legend__share">{share}</span>}
                </span>
              ) : <span />}
            </>
          );
          return (
            <li key={item.key} className="tide-legend__item" data-state={state} data-key={item.key}>
              {interactive ? (
                <button
                  type="button"
                  className="tide-legend__row"
                  aria-pressed={mode === 'toggle' ? state === 'shown' : state === 'focused'}
                  aria-describedby={item.description ? descId : undefined}
                  onClick={() => press(item.key)}
                >
                  {content}
                </button>
              ) : (
                <div className="tide-legend__row">{content}</div>
              )}
              {item.description && <p id={descId} className="tide-legend__desc">{item.description}</p>}
            </li>
          );
        })}
      </ul>
      {noneShown && (
        <p className="tide-legend__none" data-testid="legend-none">
          No categories selected. The data is still there; choose a category or Show all.
        </p>
      )}
      {interactive && (
        <div className="tide-legend__foot">
          <p className="tide-legend__summary" aria-live="polite">{summary}</p>
          <button type="button" className="tide-legend__reset" disabled={allShown} onClick={() => commit(allKeys)}>
            Show all
          </button>
        </div>
      )}
    </div>
  );
}
